import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Target,
  DollarSign,
  Calendar,
  User,
  Building2,
  Eye,
  Edit,
  TrendingUp,
  Clock,
  CheckCircle2,
  XCircle,
  RefreshCw } from
'lucide-react';

const API_URL = API_BASE || (
  typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? 'http://localhost:8001'
    : ''
);

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const stageColors = {
  'Discovery': 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-400',
  'Qualification': 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
  'Proposal': 'bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-400',
  'Negotiation': 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
  'Closed Won': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400',
  'Closed Lost': 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400'
};

const stageLabels = {
  qualification: 'Discovery',
  needs_analysis: 'Qualification',
  proposal: 'Proposal',
  negotiation: 'Negotiation',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost'
};

const normalizeStage = (stage) => String(stage || '').toLowerCase().replace(/\s+/g, '_');
const displayStage = (stage) => stageLabels[normalizeStage(stage)] || stage || 'Unknown';

export const Opportunities = () => {const { t } = useTranslation("crm");
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('open');
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewOpportunity, setShowNewOpportunity] = useState(false);
  const [newOpportunity, setNewOpportunity] = useState({
    name: '',
    value: '',
    probability: '40',
    expected_close_date: ''
  });

  const fetchOpportunities = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/crm/opportunities`;
      if (activeTab === 'won') {
        url += '?stage=closed_won';
      } else if (activeTab === 'lost') {
        url += '?stage=closed_lost';
      }
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch opportunities');
      const data = await response.json();
      setOpportunities(data);
    } catch (err) {
      toast.error('Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOpportunities();
  }, [activeTab]);

  const handleCreateOpportunity = async () => {
    if (!newOpportunity.name.trim()) {
      toast.error('Opportunity name is required');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/crm/opportunities`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: newOpportunity.name.trim(),
          value: Number(newOpportunity.value || 0),
          probability: Number(newOpportunity.probability || 40),
          expected_close_date: newOpportunity.expected_close_date || undefined
        })
      });
      if (!response.ok) throw new Error('Failed to create opportunity');
      toast.success('Opportunity created');
      setShowNewOpportunity(false);
      setNewOpportunity({ name: '', value: '', probability: '40', expected_close_date: '' });
      fetchOpportunities();
    } catch (err) {
      toast.error('Failed to create opportunity');
    }
  };

  const filteredOpportunities = opportunities.filter((opp) => {
    const matchesSearch = !searchTerm ||
    opp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    opp.company?.toLowerCase().includes(searchTerm.toLowerCase());
    const stageKey = normalizeStage(opp.stage);
    const matchesTab = activeTab === 'all' ? true :
    activeTab === 'open' ? stageKey !== 'closed_won' && stageKey !== 'closed_lost' :
    activeTab === 'won' ? stageKey === 'closed_won' :
    stageKey === 'closed_lost';
    return matchesSearch && matchesTab;
  });

  const openOpps = opportunities.filter((o) => {
    const stageKey = normalizeStage(o.stage);
    return stageKey !== 'closed_won' && stageKey !== 'closed_lost';
  });
  const stats = {
    total: opportunities.length,
    open: openOpps.length,
    openValue: openOpps.reduce((sum, o) => sum + (o.value || 0), 0),
    won: opportunities.filter((o) => normalizeStage(o.stage) === 'closed_won').length,
    wonValue: opportunities.filter((o) => normalizeStage(o.stage) === 'closed_won').reduce((sum, o) => sum + (o.value || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="crm-opportunities">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("opportunities", "Opportunities")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("trackAndManage", "Track and manage sales opportunities")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchOpportunities} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button className="gap-2 bg-teal-600 hover:bg-teal-700" onClick={() => setShowNewOpportunity(true)}>
            <Plus className="h-4 w-4" />{t("newOpportunity", "New Opportunity")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="card-elevated border-l-4 border-l-teal-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-500/10">
                <Target className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("total", "Total")}</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("open", "Open (")}{stats.open})</p>
                <p className="text-2xl font-bold">${(stats.openValue / 1000).toFixed(0)}k</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("won", "Won (")}{stats.won})</p>
                <p className="text-2xl font-bold">${(stats.wonValue / 1000).toFixed(0)}k</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <TrendingUp className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("avgDealSize", "Avg Deal Size")}</p>
                <p className="text-2xl font-bold">${stats.open > 0 ? (stats.openValue / stats.open / 1000).toFixed(0) : 0}k</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Table */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search opportunities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="open">{t("open", "Open")}</TabsTrigger>
                <TabsTrigger value="won">{t("won", "Won")}</TabsTrigger>
                <TabsTrigger value="lost">{t("lost", "Lost")}</TabsTrigger>
                <TabsTrigger value="all">{t("all", "All")}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("opportunity", "Opportunity")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("account", "Account")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("stage", "Stage")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("probability", "Probability")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("value", "Value")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("closeDate", "Close Date")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("owner", "Owner")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredOpportunities.map((opp) =>
                <tr key={opp.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium">{opp.name}</p>
                        <p className="text-xs text-muted-foreground">{t("opp", "OPP-")}{opp.id}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm">{opp.company}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Badge className={stageColors[displayStage(opp.stage)] || 'bg-gray-100 text-gray-700'}>
                        {displayStage(opp.stage)}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                          <div
                          className="h-full bg-teal-500 rounded-full"
                          style={{ width: `${opp.probability}%` }} />
                        
                        </div>
                        <span className="text-sm font-medium">{opp.probability}%</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="font-semibold text-teal-600">${opp.value?.toLocaleString() || 0}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                        {opp.expected_close ? new Date(opp.expected_close).toLocaleDateString() : 'N/A'}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{opp.owner}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredOpportunities.length === 0 &&
          <div className="py-12 text-center">
              <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("noOpportunitiesFound", "No opportunities found")}</h3>
              <p className="text-muted-foreground">{t("tryAdjustingYour", "Try adjusting your search or filter criteria")}</p>
            </div>
          }
        </CardContent>
      </Card>
      {showNewOpportunity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div role="dialog" aria-modal="true" aria-label="New Opportunity" className="w-full max-w-2xl rounded-lg border bg-background shadow-xl">
            <div className="border-b p-5">
              <h2 className="text-lg font-semibold">New Opportunity</h2>
              <p className="text-sm text-muted-foreground">Create a sales opportunity with account, stage, value, and close date.</p>
            </div>
            <div className="grid gap-4 p-5 sm:grid-cols-2">
              <Input placeholder="Opportunity name" value={newOpportunity.name} onChange={(event) => setNewOpportunity((current) => ({ ...current, name: event.target.value }))} />
              <Input placeholder="Account" disabled />
              <Input placeholder="Stage" defaultValue="Discovery" />
              <Input placeholder="Value" value={newOpportunity.value} onChange={(event) => setNewOpportunity((current) => ({ ...current, value: event.target.value }))} />
              <Input placeholder="Probability" value={newOpportunity.probability} onChange={(event) => setNewOpportunity((current) => ({ ...current, probability: event.target.value }))} />
              <Input placeholder="Expected close date" type="date" value={newOpportunity.expected_close_date} onChange={(event) => setNewOpportunity((current) => ({ ...current, expected_close_date: event.target.value }))} />
            </div>
            <div className="flex justify-end gap-2 border-t p-5">
              <Button variant="outline" onClick={() => setShowNewOpportunity(false)}>Cancel</Button>
              <Button className="bg-teal-600 hover:bg-teal-700" onClick={handleCreateOpportunity}>Create Opportunity</Button>
            </div>
          </div>
        </div>
      )}
    </div>);

};
